import { useCallback, useMemo, useState } from 'react';

import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import FormDialog from '@/components/atoms/FormDialog/FormDialog';
import { MultiSelectField, type MultiSelectOption } from '@/components/atoms/MultiSelect';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { useCloseModal } from '@/hooks/useCloseModal';
import useMembers from '@/hooks/useMembers';
import { useTeamUsers } from '@/hooks/useTeamUsers';
import { OptionBase } from '@/shared/types/common';
import { RoleType } from '@/shared/types/member';
import { Team } from '@/shared/types/team';
import { errorMessage } from '@/util/validation/helpers';
import { invitationRules } from '@/util/validation/rules/invitation-rules';

interface FormType {
  members: OptionBase[];
}

interface Props {
  team: Team;
  type: 'add' | 'remove';
}

const ModalTeamMembers = ({ type, team }: Props) => {
  const { t } = useTranslation();
  const { team_id, name } = team || {};
  const { users, addUsers, removeUser } = useTeamUsers(team_id);
  const { members } = useMembers();
  const form = useForm<FormType>({ mode: 'onChange', defaultValues: { members: [] } });
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = form;

  const closeModal = useCloseModal();
  const [isLoading, setIsLoading] = useState(false);

  const memberOptions: MultiSelectOption[] = useMemo(
    () =>
      members?.reduce((acc, user) => {
        const isNotTeamMember = users?.findIndex((u) => u.user_id === user.user_id) == -1;
        if (type === 'add' ? isNotTeamMember : !isNotTeamMember) {
          acc.push({ value: user.user_id, label: user.name, description: user.email });
        }
        return acc;
      }, [] as MultiSelectOption[]),
    [members, type, users]
  );

  const onSubmit = useCallback(
    async (form: FormType) => {
      setIsLoading(true);
      const user_ids = form.members.map((r) => r.value) as string[];
      if (type == 'add') {
        addUsers({ user_ids, team_id, name }, { onSuccess: closeModal });
      }
      if (type === 'remove') {
        await Promise.race(user_ids.map((user_id: string) => removeUser({ team_id, user_id })));
        closeModal();
      }
    },
    [addUsers, closeModal, name, removeUser, team_id, type]
  );

  const placeholder =
    type === 'add' ? t('teams.invite_members_placeholder') : t('teams.remove_members_placeholder');

  const title = type === 'add' ? t('teams.add_members_to') : t('teams.remove_members_from');

  const membersWithoutRole = useWatch({ control, name: 'members' }).reduce((acc, user) => {
    const { team_ids, role_ids, role } = members.find((m) => m.user_id === user.value);

    if (team_ids.length == 0 || (team_ids.length == 1 && team_id)) {
      if (role_ids.length === 0 && role !== RoleType.OWNER) {
        acc.push(`"${user.label}"`);
      }
    }
    return acc;
  }, [] as string[]);

  return (
    <FormDialog
      title={
        <>
          {title}
          <span className="ml-1 text-muted-foreground">{name}</span>
        </>
      }
      submitLabel={type === 'add' ? t('common.add') : t('common.remove')}
      onSubmit={handleSubmit(onSubmit)}
      isLoading={isLoading}
      danger={type === 'remove'}
      preventClose
      submitOnEnter
      contentClassName="sm:max-w-2xl"
    >
      <Form {...form}>
        <form autoComplete="off" className="space-y-6">
          <div className="input__container">
            <FormField
              control={control}
              name="members"
              rules={invitationRules(t).roles}
              render={({ field }) => {
                const selectedValues = field.value?.map((member: OptionBase) => member.value) || [];

                return (
                  <FormItem>
                    <FormLabel className="text-base font-medium">{t('common.members')}</FormLabel>
                    <FormControl>
                      <MultiSelectField
                        title={t('common.select')}
                        options={memberOptions}
                        value={selectedValues}
                        onChange={(selectedStringValues) => {
                          const selectedMembers = selectedStringValues
                            .map((value) => memberOptions.find((member) => member.value === value))
                            .filter((member): member is OptionBase => !!member);
                          field.onChange(selectedMembers);
                        }}
                        placeholder={placeholder}
                        disabled={!members}
                        error={!!errors?.members?.message}
                      />
                    </FormControl>
                    {errors?.members?.message && (
                      <FormMessage>{errorMessage({ field: errors.members })}</FormMessage>
                    )}
                  </FormItem>
                );
              }}
            />
          </div>
        </form>
      </Form>
      {membersWithoutRole.length > 0 && type === 'remove' && (
        <Alert variant="destructive">
          <AlertDescription>
            {t('teams.remove_member_warning', { 0: membersWithoutRole?.join(', '), name })}
          </AlertDescription>
        </Alert>
      )}
    </FormDialog>
  );
};

export default ModalTeamMembers;
